import { useEffect, useState, ChangeEvent, Fragment } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, gql } from '@apollo/client';
import {
    Loader,
    Header,
    Icon,
    Button,
    Message,
    Select,
    DropdownProps,
    Card,
    Image,
    Grid,
    Form,
    TextArea,
    TextAreaProps
} from 'semantic-ui-react';

import { useIsTeacher } from '../../../../hooks/isTeacher';
import { JoyyaHeader } from '../../../../components/JoyyaHeader';
import { GET_COURSE } from '../data/GET_COURSE';
import { CREATE_CLASSRECORD } from '../data/mutation_CREATE_CLASSRECORD';
import styles from './TeacherClassRecord.module.css';

interface ClassRecord {
    id: string | number;
    userId: string | number;
    confidenceScore: number | string;
    connectionScore: number | string;
    contributionScore: number | string;
    endCapacityScore: number | string;
    startCapacityScore: number | string;
    completionStatus: 'GRADUATE' | 'DROPPED' | 'CONTINUING' | 'PENDING';
    teacherComment: string;
    notes: string;
}

interface LocalRecordsState {
    [key: string]: ClassRecord;
}

const DELETE_CLASS_RECORD = gql`
    mutation DeleteClassRecord($classRecordId: Int!) {
        deleteClassRecord(classRecordId: $classRecordId)
    }
`;

const UPDATE_CLASS_RECORD = gql`
  mutation UpdateClassRecord($classRecord: ClassRecordInput!) {
    updateClassRecord(classRecord: $classRecord) {
      startCapacityScore
      endCapacityScore
      completionStatus
      connectionScore
      contributionScore
      confidenceScore
      id
      notes
      organisationId
      teacherComment
    }
  }
`;

const statusOptions = [
    { key: 'p', text: 'Pending', value: 'PENDING' },
    { key: 'g', text: 'Graduated', value: 'GRADUATE' },
    { key: 'd', text: 'Did not finish', value: 'DROPPED' },
    { key: 'c', text: 'Continuing/Repeating', value: 'CONTINUING' },
];

const droppedReasonOptions = [
    { key: 's', text: 'Scheduling clash', value: 'Scheduling clash' },
    { key: 'b', text: 'Bad attendance', value: 'Bad attendance' },
    { key: 'dwtdc', text: 'Doesn\'t want to do class', value: 'Doesn\'t want to do class' },
    { key: 'i', text: 'Personal issues', value: 'Personal Issues' },
];

const scoreError: any = (current: any, fieldName: string) => {
    return current[fieldName] &&
        !isNaN(current[fieldName] as number) &&
        (!(Number(current[fieldName]) <= 100) || !(Number(current[fieldName]) >= 0)) &&
    {
        content: 'Enter a number from 0 - 100',
        pointing: 'above',
    } || null
}

const TeacherClassRecord = () => {
    let navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { loading, error, data, refetch } = useQuery(GET_COURSE, {
        variables: { courseId: Number(id) }
    });
    const isTeacher = useIsTeacher();

    const [focusedUserId, setFocusedUserId] = useState<string | number | null>(null);
    const [updateRecord] = useMutation(UPDATE_CLASS_RECORD);
    const [deleteRecord] = useMutation(DELETE_CLASS_RECORD);
    const [createRecord] = useMutation(CREATE_CLASSRECORD);
    const [savingId, setSavingId] = useState<string | number | null>(null);
    const [localRecords, setLocalRecords] = useState<LocalRecordsState>({});

    useEffect(() => {
        if (data?.course?.classRecords) {
            const initialValues: LocalRecordsState = {};
            data.course.classRecords.forEach((rec: ClassRecord) => {
                initialValues[rec.id] = { ...rec };
            });
            setLocalRecords(initialValues);
        }
    }, [data]);

    const handleChange = (
        recordId: string | number,
        field: keyof ClassRecord,
        value: string | number | boolean | undefined
    ): void => {
        setLocalRecords((prev: LocalRecordsState) => ({
            ...prev,
            [recordId]: { ...prev[recordId], [field]: value }
        }));
    };

    const saveUpdate = async (recordId: string | number): Promise<void> => {
        setSavingId(recordId);
        const record: ClassRecord = localRecords[recordId];
        try {
            await updateRecord({
                variables: {
                    classRecord: {
                        id: recordId,
                        startCapacityScore: Number(record.startCapacityScore) || 0,
                        endCapacityScore: Number(record.endCapacityScore) || 0,
                        connectionScore: Number(record.connectionScore) || 0,
                        contributionScore: Number(record.contributionScore) || 0,
                        confidenceScore: Number(record.confidenceScore) || 0,
                        teacherComment: record.teacherComment || "",
                        notes: record.notes || "",
                        completionStatus: record.completionStatus
                    }
                }
            });
        } catch (err) {
            console.error("Update failed:", err);
        } finally {
            // wait 600 milli seconds to show the saving state
            await new Promise((resolve) => setTimeout(resolve, 600));
            setSavingId(null);
        }
    };

    if (loading) return <Loader active inline='centered' content="Loading Records..." />;
    if (error) return <Message error header='Error' content={error.message} />;

    const checker = (state: string) => (r: ClassRecord) => {
        const current: ClassRecord = localRecords[r.id] || r;
        if ((current.completionStatus || r.completionStatus) === state) {
            const student = data.course.usersWithRecords.find((p: any) => p.id === r.userId) || {};
            return student.name;
        }
        return null;
    }

    const droppedNames = data?.course?.classRecords.map(checker('DROPPED')).filter((name: string | null) => name !== null).join(', ');

    // people with records that are not in the participants list are likely to be dropped students
    const droppedStudents = data.course.usersWithRecords.filter((userWithRecord: any) => {
        // and their class record doesnt show they are dropped

        const record: ClassRecord = data.course.classRecords.find((r: ClassRecord) => r.userId === userWithRecord.id);
        if (record && record.completionStatus === 'DROPPED') {
            return false;
        }

        return !data.course.participants.some((participant: any) => participant.id === userWithRecord.id);
    });

    // fetch the class participant list and then compare to the class records to find any person without a record
    const participantsWithoutRecord = data.course.participants.filter((participant: any) => {
        return !data.course.classRecords.some((record: ClassRecord) => record.userId === participant.id);
    });

    return (
        <Fragment>
            <JoyyaHeader />
            {/* Summary of status */}
            <div className='mobile-frame'>
                <Header as='h2'>
                    <span className={styles.header}>
                        <Icon name='arrow left' className={styles.pointer} onClick={() => navigate(-1)} />
                        Class Records
                    </span>
                </Header>

                {participantsWithoutRecord.length > 0 && (<>
                    <Message warning>
                        <Message.Header>Participants without Class Records</Message.Header>
                        The following participants do not have class records.
                    </Message>
                    {participantsWithoutRecord.map((participant: any) => (
                        <div key={participant.id}>
                            {/* // create button */}
                            <button onClick={async () => {
                                const newRecord = await createRecord({
                                    variables: {
                                        studentUserId: Number(participant.id),
                                        courseId: Number(id),
                                    }
                                });

                                if (newRecord != null) {
                                    // refetch the data
                                    refetch();
                                }
                            }}>
                                + Create record for {participant.name} ({participant.identifier})
                            </button>

                        </div>
                    ))}
                    <br />
                    <br />
                </>)}

                {data?.course?.classRecords.length !== 0 && (
                    <>
                        Dropped: {droppedNames || 'None'}
                        {droppedStudents.length > 0 && (
                            <>
                                <br />
                                <Message info>
                                    <Message.Header>Note on Dropped Students</Message.Header>
                                    Students who have been removed from the class but do not have their status marked as <b>dropped</b> in their record are listed below. Please ensure to update their records accordingly or delete the records if necessary.
                                </Message>
                                {droppedStudents.map((student: any) => (
                                    <div key={student.id}>
                                        - {student.name} ({student.identifier}) (
                                        <Icon name="trash" style={{ cursor: 'pointer' }} onClick={async () => {
                                            // find the class record for this student
                                            const record: ClassRecord = data.course.classRecords.find((r: ClassRecord) => r.userId === student.id);

                                            // DELETE the record via a new mutation
                                            const id = await deleteRecord({ variables: { classRecordId: Number(record.id) } });

                                            if (id != null) {
                                                // refetch the data
                                                refetch();
                                            }
                                        }} /> delete record)
                                    </div>
                                ))}
                            </>
                        )}
                        <br />
                        {!isTeacher && (
                            <>
                            <Button onClick={async () => {
                                // redirect to classes/records
                                navigate(`/classes/records`);
                            }}
                            >
                                See all classes records
                            </Button>
                            <br />
                            </>
                        )}
                        {data.course.classRecords.some((record: ClassRecord) => record.startCapacityScore == null || isNaN(Number(record.startCapacityScore))) && (
                            <>
                                <Button onClick={async () => {
                                    await Promise.all(data.course.classRecords.map((record: ClassRecord) => {
                                        if (record.startCapacityScore == null || isNaN(Number(record.startCapacityScore))) {
                                            return updateRecord({
                                                variables: {
                                                    classRecord: {
                                                        id: record.id,
                                                        startCapacityScore: 0,
                                                        endCapacityScore: Number(record.endCapacityScore) || 0,
                                                        connectionScore: Number(record.connectionScore) || 0,
                                                        contributionScore: Number(record.contributionScore) || 0,
                                                        confidenceScore: Number(record.confidenceScore) || 0,
                                                        teacherComment: record.teacherComment || "",
                                                        notes: record.notes || "",
                                                        completionStatus: record.completionStatus
                                                    }
                                                }
                                            });
                                        } else {
                                            return Promise.resolve();
                                        }
                                    }));

                                    // refetch the data
                                    refetch();
                                }}
                                >
                                    Enable start capacity scores
                                </Button>
                                <br />
                            </>)}
                        <br />
                    </>)
                }
                <div>
                    <Grid stackable columns={3}>
                        {data?.course?.classRecords.length ? (
                            <>
                                {data?.course?.classRecords.map((record: ClassRecord) => {
                                    const student = data.course.usersWithRecords.find((p: any) => p.id === record.userId) || {};
                                    const current: ClassRecord = localRecords[record.id] || record;

                                    // If the value is dropped we should make the card light red background and only show notes and teacher comments
                                    // Make the background green if the student has graduated
                                    const shouldShowFullForm = current.completionStatus !== 'DROPPED';
                                    let cardStyle = styles.scoreCard;

                                    if (current.completionStatus === 'DROPPED') {
                                        cardStyle = styles.droppedCard;
                                    } else if (current.completionStatus === 'GRADUATE') {
                                        cardStyle = styles.graduateCard;
                                    }

                                    return (
                                        <Grid.Column key={record.id}>
                                            <Card fluid className={cardStyle}>
                                                <Card.Content onClick={() => {
                                                    if (focusedUserId === student.id) {
                                                        setFocusedUserId(null);
                                                    } else {
                                                        setFocusedUserId(student.id)
                                                    }
                                                }} style={{ cursor: 'pointer' }}>
                                                    <Image
                                                        floated='left'
                                                        size='mini'
                                                        src={student.photoUrl || 'https://react.semantic-ui.com/images/avatar/small/matt.jpg'}
                                                        circular
                                                    />
                                                    <div className={styles.cardHeader}>
                                                        <Card.Header className={styles.personName}>{student.name}</Card.Header>
                                                        <Card.Meta>{student.identifier}</Card.Meta>
                                                    </div>
                                                </Card.Content>

                                                {focusedUserId === student.id && (
                                                    <>
                                                        <Card.Content>
                                                            <Form size="small">
                                                                <Form.Field>
                                                                    <label>Completion Status</label>
                                                                    <Select
                                                                        fluid
                                                                        size='mini'
                                                                        options={statusOptions}
                                                                        value={current.completionStatus || 'PENDING'}
                                                                        onChange={(_e, { value }: DropdownProps) =>
                                                                            handleChange(record.id, 'completionStatus', value as string)
                                                                        }
                                                                    />
                                                                </Form.Field>
                                                                {current.completionStatus === 'DROPPED' && (
                                                                    <Form.Field>
                                                                        <label>Reason</label>
                                                                        <Select
                                                                            fluid
                                                                            size='mini'
                                                                            options={droppedReasonOptions}
                                                                            value={current.notes != 'Bad attendance' && current.notes != 'Scheduling clash' && current.notes != 'Doesn\'t want to do class' ? 'Personal Issues' : current.notes}
                                                                            onChange={(_e, { value }: DropdownProps) => {
                                                                                if (value === 'Bad attendance' || value === 'Scheduling clash' || value === 'Doesn\'t want to do class') {
                                                                                    handleChange(record.id, 'notes', value as string)
                                                                                } else {
                                                                                    handleChange(record.id, 'notes', '')
                                                                                }
                                                                            }}
                                                                        />
                                                                    </Form.Field>
                                                                )}

                                                                {/* A toggle to enable start capacity*/}
                                                                {current.startCapacityScore == null || isNaN(Number(current.startCapacityScore)) ? (
                                                                    null
                                                                ) : (

                                                                    <Form.Input
                                                                        label='Start capacity'
                                                                        type='number'
                                                                        disabled={current.startCapacityScore == null || isNaN(Number(current.startCapacityScore))}
                                                                        size='mini'
                                                                        min='0'
                                                                        error={scoreError(current, 'startCapacityScore')}
                                                                        max='100'
                                                                        value={current.startCapacityScore ?? null}
                                                                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(record.id, 'startCapacityScore', e.target.value)}
                                                                    />
                                                                )}
                                                                {shouldShowFullForm && (
                                                                    <>
                                                                        <Form.Input
                                                                            label='End capacity'
                                                                            type='number'
                                                                            size='mini'
                                                                            min='0'
                                                                            error={scoreError(current, 'endCapacityScore')}
                                                                            max='100'
                                                                            value={current.endCapacityScore ?? ''}
                                                                            onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(record.id, 'endCapacityScore', e.target.value)}
                                                                        />

                                                                        <div className={styles.scoreGrid}>
                                                                            <Form.Input
                                                                                label='Confidence'
                                                                                type='number'
                                                                                min='0'
                                                                                max='100'
                                                                                input={{ style: { backgroundColor: scoreError(current, 'confidenceScore') ? undefined : '#fff3cd' } }}
                                                                                error={scoreError(current, 'confidenceScore')}
                                                                                size='mini'
                                                                                value={current.confidenceScore ?? ''}
                                                                                onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(record.id, 'confidenceScore', e.target.value)}
                                                                            />
                                                                            <Form.Input
                                                                                label='Connection'
                                                                                type='number'
                                                                                min='0'
                                                                                input={{ style: { backgroundColor: scoreError(current, 'confidenceScore') ? undefined : '#d1ecf1' } }}
                                                                                max='100'
                                                                                error={scoreError(current, 'connectionScore')}
                                                                                value={current.connectionScore ?? ''}
                                                                                onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(record.id, 'connectionScore', e.target.value)}
                                                                                size='mini'
                                                                            />
                                                                            <Form.Input
                                                                                label='Contribution'
                                                                                type='number'
                                                                                min='0'
                                                                                input={{ style: { backgroundColor: scoreError(current, 'confidenceScore') ? undefined : '#f8d7da' } }}
                                                                                max='100'
                                                                                error={scoreError(current, 'contributionScore')}
                                                                                size='mini'
                                                                                value={current.contributionScore ?? ''}
                                                                                onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(record.id, 'contributionScore', e.target.value)}
                                                                            />
                                                                        </div>

                                                                        <Form.Field>
                                                                            <label>Teacher Comments</label>
                                                                            <TextArea
                                                                                rows={2}
                                                                                placeholder="Feedback for the student on a report card..."
                                                                                value={current.teacherComment || ''}
                                                                                onChange={(_e, { value }: TextAreaProps) => handleChange(record.id, 'teacherComment', value as string)}
                                                                            />
                                                                        </Form.Field>
                                                                    </>
                                                                )}

                                                                {current.notes !== 'Bad attendance' && current.notes !== 'Scheduling clash' && current.notes !== 'Doesn\'t want to do class' && (
                                                                    <Form.Field>
                                                                        <label>Notes</label>
                                                                        <TextArea
                                                                            rows={2}
                                                                            placeholder={current.notes === '' ? "Explain the issue briefly" : "Internal record keeping..."}
                                                                            value={current.notes || ''}
                                                                            onChange={(_e, { value }: TextAreaProps) => handleChange(record.id, 'notes', value as string)}
                                                                        />
                                                                    </Form.Field>)
                                                                }
                                                            </Form>
                                                        </Card.Content>

                                                        <Card.Content extra>
                                                            <Button
                                                                fluid
                                                                color='blue'
                                                                icon='save'
                                                                content='Update Record'
                                                                loading={savingId === record.id}
                                                                onClick={() => saveUpdate(record.id)}
                                                            />
                                                        </Card.Content>
                                                    </>
                                                )}
                                            </Card>
                                        </Grid.Column>
                                    );
                                })}
                            </>) : (
                            <Grid.Column>
                                <Message info header="No Class Records" content="There are no class records available for this course." />
                            </Grid.Column>
                        )}
                    </Grid>
                </div>
            </div>
        </Fragment>
    );
}

export { TeacherClassRecord };
